import React from 'react';
import { motion } from 'framer-motion';
import ScrollHeader from '../components/ScrollHeader';
import ImageModal from '../components/ImageModal';

const CodeEditor: React.FC = () => {
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);

  return (
    <div className="max-w-4xl mx-auto">
      <ScrollHeader title="Code Editor" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            View, edit, and manage the infrastructure code generated by Ana with a powerful built-in code editor.
          </p>

          <div className="bg-primary-500/10 dark:bg-primary-500/20 border-l-4 border-primary-500 p-6 rounded-r-lg mb-8">
            <h3 className="text-primary-600 dark:text-primary-400 mt-0 mb-2">💡 Key Concept</h3>
            <p className="mb-0">
              The Code Editor displays all infrastructure code generated by Ana, allowing you to review, 
              understand, and customize it before deployment. It provides syntax highlighting, code formatting, 
              and direct access to visualization tools.
            </p>
          </div>

          <h2>Overview</h2>
          <p>
            After Ana generates infrastructure code through the Chat interface, you can view and manage it in the Code Editor. 
            This is where you can see the complete code structure, make manual adjustments if needed, and access 
            visualization features.
          </p>

          <div 
            className="my-8 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            onClick={() => setSelectedImage('/screenshots/code_editor/code_editor.png')}
          >
            <img 
              src="/screenshots/code_editor/code_editor.png" 
              alt="Code Editor Interface" 
              className="w-full"
            />
          </div>

          <h2>Key Features</h2>

          <h3>Syntax Highlighting</h3>
          <p>
            The Code Editor provides intelligent syntax highlighting for multiple infrastructure-as-code languages:
          </p>
          <ul>
            <li><strong>Terraform (HCL):</strong> Full support for HashiCorp Configuration Language</li>
            <li><strong>YAML:</strong> For Kubernetes manifests, Docker Compose, and CI/CD pipelines</li>
            <li><strong>JSON:</strong> For CloudFormation templates and configuration files</li>
            <li><strong>Dockerfile:</strong> Specialized highlighting for container definitions</li>
          </ul>

          <h3>File Organization</h3>
          <p>
            Generated code is automatically organized into a logical file structure:
          </p>
          <ul>
            <li>Main configuration files (e.g., main.tf, variables.tf)</li>
            <li>Module definitions and reusable components</li>
            <li>Environment-specific configurations</li>
            <li>Documentation and README files</li>
          </ul>

          <h3>Visual Infrastructure Editor</h3>
          <p>
            The Code Editor includes an "eye button" (👁️) that opens the Visualisation tool, allowing you to:
          </p>
          <ul>
            <li>See a visual representation of your infrastructure</li>
            <li>Understand component relationships and dependencies</li>
            <li>Add new nodes and resources visually</li>
            <li>Preview deployment plans and costs</li>
          </ul>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
            <h4 className="text-blue-800 dark:text-blue-400 mt-0 mb-2">🔍 Visualization Access</h4>
            <p className="mb-0 text-blue-700 dark:text-blue-300">
              Click the eye button (👁️) in the Code Editor to open the Visualisation tool and see your 
              infrastructure in a visual diagram format. This makes it easier to understand complex architectures 
              and identify potential issues.
            </p>
          </div>

          <h2>Working with Generated Code</h2>

          <h3>Understanding the Code</h3>
          <p>
            Ana generates well-documented, production-ready code with:
          </p>
          <ul>
            <li><strong>Inline Comments:</strong> Explanations of key configurations and decisions</li>
            <li><strong>Variable Definitions:</strong> Clear variable names with descriptions</li>
            <li><strong>Module Documentation:</strong> README files for custom modules</li>
            <li><strong>Best Practices:</strong> Code follows industry standards and conventions</li>
          </ul>

          <h3>Making Manual Edits</h3>
          <p>
            While Ana generates comprehensive code, you can make manual adjustments:
          </p>
          <ul>
            <li>Modify variable values for your specific environment</li>
            <li>Adjust resource configurations (instance types, sizes, etc.)</li>
            <li>Add custom tags or labels</li>
            <li>Fine-tune security settings</li>
          </ul>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-6">
            <h4 className="text-yellow-800 dark:text-yellow-400 mt-0 mb-2">⚠️ Note on Manual Edits</h4>
            <p className="mb-0 text-yellow-700 dark:text-yellow-300">
              If you make manual edits to the code, be aware that asking Ana to regenerate or modify the code 
              may overwrite your changes. Consider using version control or saving important customizations separately.
            </p>
          </div>

          <h3>Code Validation</h3>
          <p>
            The Code Editor includes built-in validation to help catch errors:
          </p>
          <ul>
            <li>Syntax checking for infrastructure-as-code languages</li>
            <li>Linting for common mistakes and anti-patterns</li>
            <li>Warnings for potential security issues</li>
            <li>Suggestions for optimization opportunities</li>
          </ul>

          <h2>Code Editor Workflow</h2>

          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-6">
            <h3 className="text-gray-700 dark:text-gray-300 mt-0 mb-4">Typical Workflow:</h3>
            <ol className="space-y-3 mb-0">
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Generate Code:</strong> Use Chat to describe your infrastructure needs
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Review in Editor:</strong> Navigate to Code Editor to see the generated code
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Visualize:</strong> Click the eye button to open the visual infrastructure diagram
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Make Adjustments:</strong> Edit code manually or ask Ana for modifications
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Validate:</strong> Check for syntax errors and warnings
              </li>
              <li className="text-gray-600 dark:text-gray-400">
                <strong>Deploy:</strong> Use the Visualisation tool to preview and deploy
              </li>
            </ol>
          </div>

          <h2>Next Steps</h2>
          <p>
            From the Code Editor, you can:
          </p>
          <ul>
            <li>
              <strong>Open Visualisation:</strong> Click the eye button to see your infrastructure visually
            </li>
            <li>
              <strong>Return to Chat:</strong> Ask Ana to make modifications or add features
            </li>
            <li>
              <strong>Set up Integrations:</strong> Configure cloud provider credentials for deployment
            </li>
            <li>
              <strong>Deploy:</strong> Use the Visualisation tool to preview costs and deploy
            </li>
          </ul>

          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mt-8">
            <h3 className="text-green-800 dark:text-green-400 mt-0 mb-2">✨ Pro Tip</h3>
            <p className="mb-0 text-green-700 dark:text-green-300">
              Use the Code Editor in combination with the Visualisation tool to get both a code-level and 
              architectural-level understanding of your infrastructure. This dual view helps catch issues 
              early and ensures your infrastructure meets all requirements.
            </p>
          </div>
        </div>
      </motion.div>

      <ImageModal
        isOpen={selectedImage !== null}
        src={selectedImage || ''}
        onClose={() => setSelectedImage(null)}
        alt="Screenshot"

      />
    </div>
  );
};

export default CodeEditor;
